import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMagnifyingGlass,
  faChevronLeft,
  faChevronRight,
  faBan,
  faRotateLeft,
} from "@fortawesome/free-solid-svg-icons";
import { COLUMNS, getPairIds } from "./AdminDuplicateUsers";

function MemberFields({ member, matches }) {
  return (
    <div className="flex-1 grid grid-cols-2 gap-x-3 gap-y-1 text-sm">
      {COLUMNS.map((col) => (
        <div
          key={col.key}
          className={`rounded px-2 py-1 border ${
            matches?.[col.key]
              ? "bg-green-50 border-green-200"
              : "bg-red-50 border-red-200"
          }`}
        >
          <p className="text-[10px] uppercase tracking-wide text-gray-500">
            {col.label}
          </p>
          <p className="text-gray-900">{member[col.key] ?? ""}</p>
        </div>
      ))}
    </div>
  );
}

function GroupCard({
  group,
  selection,
  onToggleMember,
  onRestore,
  restoring,
  onDismissSelected,
  onOpenCompare,
  dismissing,
  showDismissed,
}) {
  const hasPairSelected =
    selection.groupKey === group.groupKey && selection.ids.length === 2;

  return (
    <div className="border border-gray-300 rounded-md mb-3 p-3">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div>
          <p className="font-semibold text-gray-800">
            {group.firstName} {group.lastName}
          </p>
          <p className="text-xs text-gray-500">
            {group.members?.length ?? 0} accounts
          </p>
        </div>
      </div>
      {hasPairSelected && (
        <div className="-mx-3 mb-2 px-3 py-2.5 bg-red-50 border-y border-red-200">
          <p className="text-xs text-gray-600 font-medium text-center mb-2">
            2 accounts selected
          </p>
          <div className="flex items-center justify-center gap-2 flex-wrap">
            <button
              onClick={() => onDismissSelected(group)}
              disabled={dismissing}
              className="text-sm font-medium px-4 py-1.5 rounded border border-gray-400 bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              Not a duplicate
            </button>
            <button
              onClick={() => onOpenCompare(group)}
              className="text-sm font-semibold px-4 py-1.5 rounded bg-red-500 text-white shadow-sm hover:bg-red-600"
            >
              Compare &amp; Merge
            </button>
          </div>
        </div>
      )}
      <div className="divide-y divide-gray-100">
        {group.members?.map((member) => {
          const isSelected =
            selection.groupKey === group.groupKey &&
            selection.ids.includes(member.userId);
          return (
            <div key={member.userId} className="flex items-start gap-2 py-2">
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => onToggleMember(group, member.userId)}
                className="mt-1"
              />
              <MemberFields member={member} matches={group.matches} />
            </div>
          );
        })}
      </div>
      {showDismissed && group.dismissedPairs?.length > 0 && (
        <div className="-mx-3 -mb-3 mt-2 px-3 py-2 bg-gray-50 border-t border-gray-200 flex flex-col gap-2 rounded-b-md">
          {group.dismissedPairs.map((pair, i) => {
            const [idA, idB] = getPairIds(pair);
            return (
              <div
                key={`${idA}-${idB}-${i}`}
                className="flex items-center justify-between gap-2"
              >
                <span className="flex items-center gap-2 text-xs text-gray-500">
                  <FontAwesomeIcon icon={faBan} className="text-gray-400" />
                  Dismissed:
                  <span className="px-1.5 py-0.5 rounded bg-white border border-gray-200 font-medium text-gray-600">
                    #{idA}
                  </span>
                  &harr;
                  <span className="px-1.5 py-0.5 rounded bg-white border border-gray-200 font-medium text-gray-600">
                    #{idB}
                  </span>
                </span>
                <button
                  onClick={() => onRestore(idA, idB)}
                  disabled={restoring}
                  className="flex items-center gap-1.5 text-xs font-medium px-3 py-1 rounded-full border border-blue-300 text-blue-600 hover:bg-blue-50 disabled:opacity-50 shrink-0"
                >
                  <FontAwesomeIcon icon={faRotateLeft} />
                  Restore
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function DuplicateUsersMobileList({
  groups,
  searchQuery,
  onSearchChange,
  showDismissed,
  onShowDismissedChange,
  loading,
  error,
  page,
  totalPages,
  totalResults,
  startRecord,
  endRecord,
  onPageChange,
  selection,
  onToggleMember,
  onRestore,
  restoring,
  onDismissSelected,
  onOpenCompare,
  dismissing,
}) {
  return (
    <div className="pb-40">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-base font-semibold">
          Potential Duplicate Accounts
        </h1>
      </div>

      <div className="relative mb-3">
        <FontAwesomeIcon
          icon={faMagnifyingGlass}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
        />
        <input
          type="text"
          placeholder="Search..."
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-red-400"
        />
      </div>

      <label className="flex items-center gap-2 text-sm text-gray-700 mb-4">
        <input
          type="checkbox"
          checked={showDismissed}
          onChange={(e) => onShowDismissedChange(e.target.checked)}
        />
        Show dismissed
      </label>

      {loading && <p className="text-center py-8 text-gray-500">Loading...</p>}
      {error && !loading && (
        <p className="text-center py-8 text-red-500">{error}</p>
      )}
      {!loading && !error && (
        <div>
          {groups.length === 0 ? (
            <p className="text-center py-8 text-gray-500">No results found</p>
          ) : (
            groups.map((group) => (
              <GroupCard
                key={group.groupKey}
                group={group}
                selection={selection}
                onToggleMember={onToggleMember}
                onRestore={onRestore}
                restoring={restoring}
                onDismissSelected={onDismissSelected}
                onOpenCompare={onOpenCompare}
                dismissing={dismissing}
                showDismissed={showDismissed}
              />
            ))
          )}
        </div>
      )}

      {totalResults > 0 && (
        <div className="mt-4 flex flex-col items-center gap-2 text-sm">
          <p className="text-gray-600">
            Showing {startRecord}&ndash;{endRecord} of {totalResults}
          </p>
          <div className="flex items-center gap-3">
            <button
              onClick={() => onPageChange((p) => p - 1)}
              disabled={page === 1}
              className="px-3 py-1.5 rounded border border-gray-300 disabled:opacity-40 hover:bg-gray-100"
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>
            <span className="text-gray-700">
              Page {page} of {totalPages}
            </span>
            <button
              onClick={() => onPageChange((p) => p + 1)}
              disabled={page === totalPages}
              className="px-3 py-1.5 rounded border border-gray-300 disabled:opacity-40 hover:bg-gray-100"
            >
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
